import type { AskRevQueryResult } from '@/types/askRevFilters';
import { gql, useMutation } from '@apollo/client';

// Valid sort fields for frontend validation
const VALID_SORT_FIELDS = new Set([
	// Rate fields
	'adr',
	'adr_live',
	'current_rate',
	'current_rate_live',
	'rec_rate',
	'derived_rate',
	'conservative_rate',
	'neutral_rate',
	'aggressive_rate',
	'prev_rec_rate',
	'last_current_rate',
	'rate_variance',
	'market_rate',
	// Occupancy fields
	'occ_pct',
	'occ_pct_live',
	'occ_pct_lyst',
	'occ_pct_lyf',
	'fcst_occ_pct',
	// Revenue fields
	'current_revenue',
	'current_revenue_live',
	'revpar',
	'revpar_live',
	'revpar_lyst',
	'revpar_lyf',
	'fcst_rev',
	// Date and priority
	'stay_date',
	'priority',
	// Pickup fields
	'pkp_day_1',
	'pkp_day_1_ly',
	'pkp_day_3',
	'pkp_day_3_ly',
	'pkp_day_7',
	'pkp_day_7_ly',
	'pkp_doa_ly',
	'transient_pkp_day_1',
	'transient_pkp_day_3',
	'transient_pkp_day_7',
	'group_pkp_day_1',
	'group_pkp_day_3',
	'group_pkp_day_7',
	// Forecast fields
	'transient_fcst',
	'transient_fcst_event',
	'unc_transient_fcst',
	'total_fcst_incl_group',
	'a_fcst_sell',
	'b_fcst_sell',
	'c_fcst_sell',
	'd_fcst_sell',
	'e_fcst_sell',
	'f_fcst_sell',
	'g_fcst_sell',
	'h_fcst_sell',
	// Inventory fields
	'available_rooms_live',
	'total_capacity',
	'transient_capacity_live',
	'group_blocked_live',
	'group_booked_live',
	'ooo_live',
	'overbooking',
	// OTB fields
	'current_otb_live',
	'current_otb_lyst',
	'current_otb_lyf',
	// Events
	'event_count',
	// YoY rate fields
	'yoy_rate_lyst',
	'yoy_rate_lyf',
]);

/**
 * Validates that a sort field is valid for frontend use
 */
const isValidSortField = (sortBy: string | null | undefined): boolean => {
	if (!sortBy) return true; // null is valid (no sorting)

	// Check basic fields
	if (VALID_SORT_FIELDS.has(sortBy)) return true;

	// Check competitor rate fields (compRates.{hotelId})
	return /^compRates\.\d+$/.test(sortBy);
};
const INTERPRET_ASK_REV_QUERY = gql`
  mutation InterpretAskRevQuery($input: AskRevQueryInput!) {
    interpretAskRevQuery(input: $input) {
      filters {
        field
        operator
        value
      }
      summary
      suggestion
      requiresSpecificCompetitors
      # NEW: Request sorting parameters
      sortBy
      sortDirection
      sortLimit
    }
  }
`;
export function useInterpretAskRevQuery() {
	const [interpretMutation, { loading }] = useMutation(INTERPRET_ASK_REV_QUERY);
	const interpretQuery = async (
		query: string,
		compSet?: Array<{ hotelId: number; name: string }>,
	): Promise<AskRevQueryResult | null> => {
		try {
			const { data } = await interpretMutation({
				variables: {
					input: { query, compSet },
				},
			});
			const result = data?.interpretAskRevQuery;
			if (result) {
				// Validate sorting fields on frontend as additional safety
				const hasSorting = result.sortBy && result.sortDirection;
				let finalSortBy = result.sortBy;
				let finalSortDirection = result.sortDirection;
				let updatedSummary = result.summary || '';

				if (hasSorting && !isValidSortField(result.sortBy)) {
					// Use filters but ignore invalid sorting
					finalSortBy = null;
					finalSortDirection = null;
					updatedSummary = `${updatedSummary} (Sorting not available for requested field)`;
				}

				// Validate sort limit is reasonable
				let finalSortLimit = result.sortLimit;
				if (finalSortLimit && (finalSortLimit <= 0 || finalSortLimit > 1000)) {
					finalSortLimit = null;
				}

				return {
					filters: result.filters || [],
					summary: updatedSummary,
					suggestion: result.suggestion,
					requiresSpecificCompetitors: result.requiresSpecificCompetitors,
					// NEW: Include validated sorting parameters from AI response
					sortBy: finalSortBy,
					sortDirection: finalSortDirection,
					sortLimit: finalSortLimit,
				};
			}
			return null;
		} catch (err) {
			console.error('interpretAskRevQuery failed:', err);
			return null;
		}
	};
	return { interpretQuery, queryLoading: loading };
}
